import type { RecalcAdjustment, RecalculateCommissionResult } from './commissions';

export interface RecalcNotice {
  tone: 'info' | 'success' | 'warning';
  message: string;
}

const rupees = (amount: string) => {
  const n = Number(amount);
  return Number.isFinite(n)
    ? `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : `₹${amount}`;
};

const party = (a: RecalcAdjustment) => (a.party === 'posp' ? 'agent' : 'broker');

function describeAdjustment(a: RecalcAdjustment): string {
  if (a.kind === 'clawback') {
    return `a clawback of ${rupees(a.amount)} from the ${party(a)} (netted against their next payout)`;
  }
  return `an adjustment of ${rupees(a.amount)} for the ${party(a)}`;
}

/**
 * W0-A (B3): the sentence the policy page shows after "Recalculate Commission".
 * A blanket "recalculated" hid the two cases that matter for money: nothing was
 * posted, or a commission that was already paid was corrected by a clawback /
 * adjustment rather than paid again.
 */
export function describeRecalcOutcome(result: RecalculateCommissionResult | undefined | null): RecalcNotice {
  const outcome = result?.recalc_outcome;
  const adjustments = result?.recalc_adjustments ?? [];
  switch (outcome) {
    case 'unchanged':
      return { tone: 'info', message: 'Commission unchanged — the latest rules give the same result, so nothing was posted.' };
    case 'concurrent':
      return { tone: 'info', message: 'Another recalculation finished first; showing its result. Nothing extra was posted.' };
    case 'adjusted':
      return {
        tone: 'warning',
        message: `This commission was already paid, so it was not paid again. Posted ${adjustments
          .map(describeAdjustment)
          .join(' and ')}.`,
      };
    case 'calculated':
      return { tone: 'success', message: 'Commission calculated.' };
    default:
      return { tone: 'success', message: 'Commission recalculated.' };
  }
}
